import throttle from 'lodash/throttle';
import { toAnalyticsEvent } from './analytics_event';

const VARIANT_PROGRESS_THROTTLING = 5000;

/**
 * Variant events require a little logistics to not overload the server
 * with too many progress event, which in turn require care to properly
 * handle the ending of a variant to not send duplicate/superfluous events
 * @param {Function} sendEvent - The method for sending the analytics event
 */
export function forwardVariantEvents(sendEvent) {
  // Track variant progress so we can know
  // if the viewer is going forward or not
  let variantProgress = 0;
  // Track furthest viewing progress to help
  // backend computations
  let furthestProgress = 0;
  // Sometimes the player sends extra progress events
  // after the end is reached so we need to ignore those
  let reachedEnd = false;

  document.addEventListener('sw.variant.start', event => {
    // Reset the tracked variant progress
    variantProgress = 0;
    reachedEnd = false;

    sendEvent(toAnalyticsEvent(event));
  });

  // `sw.variant.progress` events are fired at a high rate
  // by the player. To avoid overloading the analytics endpoint
  // we throttle sending them to the server, unless they're about going
  // back in the video or reaching the end of the video
  document.addEventListener('sw.variant.progress', event => {
    if (!reachedEnd) {
      if (furthestProgress < event.detail.current) {
        furthestProgress = event.detail.current;
      }
    }

    const analyticsEvent = toAnalyticsEvent(event, {
      furthest: furthestProgress
    });

    // Schedule the sending of the analytics event
    //
    // The throttled function may run the last call after a new
    // variant has started, clearing the current `reachedEnd`.
    // This is why we need to pass the value of `reachedEnd`
    // at the time of scheduling rather than blindly run it.
    //
    // TODO: Once the `variant.end` event is in, remove the trailing
    // call (`{trailing: false}` option for `throttle`) and handle
    // sending a final `progress` event from the `variant.end`.
    // This will ensure both ending of play, cut on click and pause at end
    // are handled correctly

    // In case of ending or going back send the event immediately
    // >= in case of mis-rounding from the player
    if (event.detail.current >= event.detail.total) {
      // Throttle and flush so that later on, the `flush`
      // can be handled by the `variant.end`
      throttledSendEvent(analyticsEvent);
      throttledSendEvent.flush();
      // Write one last
      variantProgress = event.detail.current;
      reachedEnd = true;
    } else if (event.detail.current < variantProgress) {
      // Send any remaining progress event to get
      // a progress event for current progress
      throttledSendEvent.flush();
      // Before sending an event immediately for having gone back
      // Somehow calling throttledSendEvent doesn't run a leading call
      sendEvent(analyticsEvent);
    } else {
      throttledSendEvent(analyticsEvent);
    }

    variantProgress = event.detail.current;
  });

  const throttledSendEvent = throttle((analyticsEvent, reachedEnd) => {
    if (!reachedEnd) {
      sendEvent(analyticsEvent);
    }
  }, VARIANT_PROGRESS_THROTTLING);
}
